import { useCallback, useMemo } from 'react';
import {
  useAuth0,
  type AppState,
  type LogoutOptions,
  type RedirectLoginOptions,
} from '@auth0/auth0-react';
import {
  AUTH0_AUDIENCE,
  AUTH0_SCOPE,
  AUTH0_ORGANIZATION_KEY,
  AUTH0_ROLES_KEY,
  AUTH0_ADMIN_ROLE_NAME,
} from './config';

type UseAuth = {
  isLoading: boolean;
  isAuthenticated: boolean;
  currentUser: User | null;
  loginWithRedirect: (
    options?: RedirectLoginOptions<AppState>
  ) => Promise<void>;
  logout: (options?: LogoutOptions) => Promise<void>;
  getAccessToken: () => Promise<string>;
};

type User = {
  userId: string;
  userName: string;
  organizationDisplayName: string;
  isAdmin: boolean;
};

const useAuth = (): UseAuth => {
  const {
    isLoading,
    isAuthenticated,
    user,
    loginWithRedirect,
    logout,
    getAccessTokenSilently,
  } = useAuth0();

  const currentUser = useMemo((): User | null => {
    return user
      ? {
          userId: user.sub || '',
          userName: user.name || '',
          organizationDisplayName:
            user[AUTH0_ORGANIZATION_KEY]?.display_name ||
            user[AUTH0_ORGANIZATION_KEY]?.name ||
            '',
          isAdmin: !!user[AUTH0_ROLES_KEY]?.includes(AUTH0_ADMIN_ROLE_NAME),
        }
      : null;
  }, [user]);

  const getAccessToken = useCallback(async (): Promise<string> => {
    return getAccessTokenSilently({
      authorizationParams: {
        audience: AUTH0_AUDIENCE,
        scope: AUTH0_SCOPE,
      },
    }).then((token: string | undefined): string => {
      if (!token) {
        throw new Error('Cannot get Auth0 access token.');
      }
      return token;
    });
  }, [getAccessTokenSilently]);

  return {
    isLoading,
    isAuthenticated,
    currentUser,
    loginWithRedirect,
    logout,
    getAccessToken,
  };
};

export { useAuth };
